import React from "react";
import { Link } from "react-router-dom";
import { PawPrint, ArrowLeft } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen font-['Figtree']" style={{ background: "#FAF9F6" }}>
      <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-white/40">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="privacy-home-link">
            <div className="w-8 h-8 rounded-full bg-[#FF8A65] flex items-center justify-center">
              <PawPrint size={18} className="text-white" strokeWidth={2.5} />
            </div>
            <span className="font-['Fredoka_One'] text-xl text-slate-800 tracking-wide">Pet Atlas</span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#FF8A65] transition-colors" data-testid="privacy-back-btn">
            <ArrowLeft size={16} /> Back
          </Link>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="privacy-policy-page">
        <h1 className="text-3xl sm:text-4xl font-black text-slate-800 font-['Nunito'] mb-2">Privacy Policy</h1>
        <p className="text-sm text-slate-500 mb-8">Last updated: April 2026</p>

        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8 space-y-6 text-slate-700 leading-relaxed">
          <section>
            <h2 className="text-lg font-bold text-slate-800 font-['Nunito'] mb-2">1. Who we are</h2>
            <p>Pet Atlas ("we", "our", "us") is a pet-care management service. This Privacy Policy explains how we collect, use, and protect information you provide when you use Pet Atlas.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-800 font-['Nunito'] mb-2">2. Information we collect</h2>
            <ul className="list-disc pl-5 space-y-1 text-sm">
              <li><strong>Account info:</strong> your name and email. If you sign in with Google, we also receive your profile picture from your Google account.</li>
              <li><strong>Authentication:</strong> for email/password accounts, we store a bcrypt hash of your password — never the plaintext. For Google OAuth accounts, we store only the session identifier Google returns.</li>
              <li><strong>Pet data:</strong> pet profiles, vaccinations, weight logs, medical notes, vet records, and reminders you create.</li>
              <li><strong>Payment metadata:</strong> Stripe customer ID, session IDs, and subscription status (we never store card numbers or CVVs).</li>
              <li><strong>Usage logs:</strong> basic technical logs (IP, browser, timestamps) for security and reliability.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-800 font-['Nunito'] mb-2">3. How we use your data</h2>
            <ul className="list-disc pl-5 space-y-1 text-sm">
              <li>To provide the Pet Atlas service (reminders, passport, travel, emergency mode).</li>
              <li>To send transactional emails (digest reminders, weekly summaries) that you can disable at any time.</li>
              <li>To process subscription payments via Stripe.</li>
              <li>To improve reliability and prevent abuse.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-800 font-['Nunito'] mb-2">4. Sharing</h2>
            <p>We do <strong>not</strong> sell your data. We share only with processors we need to run the service: Google (authentication), Stripe (payments), Resend (email delivery), and our hosting provider.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-800 font-['Nunito'] mb-2">5. Public passport links</h2>
            <p>When you generate a shareable passport link, anyone with the link can view that pet's public info (name, species, vaccinations, vet contact). You can disable a link at any time by generating a new one or deleting the pet.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-800 font-['Nunito'] mb-2">6. Your rights</h2>
            <p>You may export or delete your data at any time. Use <strong>Settings → Delete Account</strong> to permanently remove your account, pets, reminders, and sessions. If you are a resident of California (CCPA) or the European Economic Area / UK (GDPR), you additionally have the right to access, correct, or port your personal data — email us to make a request.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-800 font-['Nunito'] mb-2">7. Security</h2>
            <p>Sessions are stored server-side and transmitted over HTTPS. Email/password accounts use bcrypt hashing — we never store plaintext passwords. Google OAuth users have no password stored with us. We use reasonable administrative and technical safeguards, but no system is perfectly secure.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-800 font-['Nunito'] mb-2">8. Data retention</h2>
            <p>We keep your account data for as long as your account is active. When you delete your account:</p>
            <ul className="list-disc pl-5 space-y-1 text-sm mt-2">
              <li><strong>Active data</strong> (pets, reminders, records, sessions, payment metadata) is purged immediately.</li>
              <li><strong>Encrypted backup copies</strong> may persist for up to <strong>30 days</strong> in our hosting provider's backup systems, after which they are permanently overwritten.</li>
              <li><strong>Anonymized, aggregated metrics</strong> (e.g. "number of reminders completed across all users in a month") may be retained indefinitely — these contain no personally identifiable information.</li>
              <li>Legal or regulatory records (e.g. Stripe transaction records required for tax compliance) are retained as required by applicable law.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-800 font-['Nunito'] mb-2">9. Changes to this Policy</h2>
            <p>We may update this Privacy Policy from time to time. For material changes, we will provide at least 14 days' notice by email before the changes take effect.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-800 font-['Nunito'] mb-2">10. Contact</h2>
            <p>Questions? Reach out at <a href="mailto:support@petatlas.app" className="text-[#FF8A65] font-semibold">support@petatlas.app</a>.</p>
          </section>
        </div>

        <div className="mt-8 text-center">
          <Link to="/terms" className="text-sm text-slate-500 hover:text-[#FF8A65] font-medium">Read Terms of Service →</Link>
        </div>
      </main>
    </div>
  );
}
